import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Sparkles } from "lucide-react";
import { useAppDispatch } from "../../../app/hooks";
import { ROUTES } from "../../../routes";
import { ImportResult } from "../models/ImportResult";
import { ImportResultItem } from "../models/ImportResultItem";
import { resetImport } from "../assistant-import-slice";

interface ImportResultSummaryProps {
  result: ImportResult;
}

function ResultList({ title, items }: { title: string; items: ImportResultItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="font-semibold">{title}</h3>
      <ul className="mt-1 space-y-1">
        {items.map((item) => (
          <li key={item.tempId} className="flex items-start gap-2 text-sm">
            {item.error === null ? (
              <Check size={16} className="mt-0.5 shrink-0 text-green-700 dark:text-green-400" />
            ) : (
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-700 dark:text-red-400" />
            )}
            <span>
              {item.label}
              {item.error !== null && (
                <span className="text-red-700 dark:text-red-400"> — {item.error}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ImportResultSummary({ result }: ImportResultSummaryProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const allItems = [...result.members, ...result.groups, ...result.sessions];
  const failedCount = allItems.filter((item) => item.error !== null).length;

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Sparkles size={20} strokeWidth={2} />
        </span>
        <div>
          <h2 className="text-lg font-semibold leading-tight">
            {t("assistantImport.result.title")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {failedCount === 0
              ? t("assistantImport.result.allCreated")
              : t("assistantImport.result.partiallyCreated", { count: failedCount })}
          </p>
        </div>
      </div>

      <ResultList title={t("assistantImport.review.members")} items={result.members} />
      <ResultList title={t("assistantImport.review.groups")} items={result.groups} />
      <ResultList title={t("assistantImport.review.sessions")} items={result.sessions} />

      <div className="flex flex-wrap gap-3">
        <button type="button" className="btn btn-primary" onClick={() => navigate(ROUTES.MEMBERS)}>
          {t("assistantImport.result.goToMembers")}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(ROUTES.SESSIONS)}>
          {t("assistantImport.result.goToSessions")}
        </button>
        <button type="button" className="btn btn-text" onClick={() => dispatch(resetImport())}>
          {t("assistantImport.result.importMore")}
        </button>
      </div>
    </div>
  );
}
